/**
 * German compound-word splitter.
 *
 * Decomposes a long word into its base lemmas by matching prefixes against
 * a dictionary. Handles German Fugen-S (linking 's', 'n', 'en', 'es') by
 * dropping leading connectors when recursing into the rest.
 *
 * Example: "arbeitszeit" + dict {arbeit, zeit} → ["arbeit", "zeit"].
 * Example: "schichtplanung" + dict {schicht, planung} → ["schicht", "planung"].
 *
 * Memoized on the input word for the lifetime of the call.
 */
export function splitCompound(
  word: string,
  dict: ReadonlySet<string>,
  opts: { minLength?: number; minPartLength?: number } = {}
): string[] {
  const minLength = opts.minLength ?? 6;
  const minPartLength = opts.minPartLength ?? 3;
  if (!word || word.length < minLength) return [word];

  const memo = new Map<string, string[] | null>();

  const decompose = (w: string): string[] | null => {
    if (memo.has(w)) return memo.get(w)!;
    if (dict.has(w)) {
      memo.set(w, [w]);
      return [w];
    }
    if (w.length < minLength) {
      memo.set(w, null);
      return null;
    }
    for (let i = w.length - 1; i >= minPartLength; i--) {
      const prefix = w.slice(0, i);
      if (!dict.has(prefix)) continue;
      let rest = w.slice(i);
      // Drop Fugen-S / Fugen-N etc.
      for (const fuge of ['en', 'es', 's', 'n']) {
        if (rest.startsWith(fuge) && rest.length - fuge.length >= minPartLength) {
          const trimmed = rest.slice(fuge.length);
          const sub = decompose(trimmed);
          if (sub) {
            const result = [prefix, ...sub];
            memo.set(w, result);
            return result;
          }
        }
      }
      const sub = decompose(rest);
      if (sub) {
        const result = [prefix, ...sub];
        memo.set(w, result);
        return result;
      }
    }
    memo.set(w, null);
    return null;
  };

  const result = decompose(word);
  return result ?? [word];
}

/**
 * Apply splitCompound to every token in the query. Returns the union of
 * all parts found across tokens (deduplicated).
 */
export function expandCompoundParts(
  tokens: ReadonlyArray<string>,
  dict: ReadonlySet<string>
): string[] {
  const parts = new Set<string>();
  for (const t of tokens) {
    const split = splitCompound(t, dict);
    if (split.length > 1) {
      for (const p of split) {
        if (p.length >= 3) parts.add(p);
      }
    }
  }
  return Array.from(parts);
}
